import { isCordovaApp } from './index'

type T_CookieManagementPlugin = {
  flush?: (
    arg0?: unknown,
    success?: () => void,
    error?: (e: unknown) => void,
  ) => void
}

type T_CordovaWindow = Window & {
  cordova?: {
    platformId?: string
    plugins?: {
      CookieManagementPlugin?: T_CookieManagementPlugin
    }
  }
}

const IS_CORDOVA_BUILD = isCordovaApp
const DEVICE_READY_TIMEOUT_MS = 5000
const FLUSH_TIMEOUT_MS = 2500
let deviceReadyPromise: Promise<void> | null = null
let flushInFlightPromise: Promise<boolean> | null = null

const getCordovaWindow = (): T_CordovaWindow | null => {
  if (typeof window === 'undefined') return null
  return window as T_CordovaWindow
}

const waitForDeviceReady = async (
  timeoutMs = DEVICE_READY_TIMEOUT_MS,
): Promise<boolean> => {
  if (!IS_CORDOVA_BUILD) return false

  const cordovaWindow = getCordovaWindow()
  if (cordovaWindow?.cordova?.platformId) return true

  if (typeof document === 'undefined') return false

  if (!deviceReadyPromise) {
    deviceReadyPromise = new Promise((resolve) => {
      document.addEventListener('deviceready', () => resolve(), { once: true })
    })
  }

  let timeoutId: ReturnType<typeof setTimeout> | null = null
  const timedOut = new Promise<boolean>((resolve) => {
    timeoutId = setTimeout(() => resolve(false), timeoutMs)
  })

  const isReady = await Promise.race([
    deviceReadyPromise.then(() => true),
    timedOut,
  ])

  if (timeoutId) clearTimeout(timeoutId)
  return isReady
}

const getCookieManagerPlugin = (): T_CookieManagementPlugin | null => {
  const cordovaWindow = getCordovaWindow()
  const isAndroid = cordovaWindow?.cordova?.platformId === 'android'
  if (!isAndroid) return null

  return cordovaWindow?.cordova?.plugins?.CookieManagementPlugin ?? null
}

export const flushCordovaCookies = async (): Promise<boolean> => {
  if (!IS_CORDOVA_BUILD) return false

  if (flushInFlightPromise) {
    return await flushInFlightPromise
  }

  flushInFlightPromise = (async () => {
    const isReady = await waitForDeviceReady()
    if (!isReady) {
      console.warn('[cordova][cookie-manager] deviceready timeout, skip flush')
      return false
    }

    const platformId = getCordovaWindow()?.cordova?.platformId
    if (platformId !== 'android') {
      console.info('[cordova][cookie-manager] skip flush: platform is not android')
      return false
    }

    const plugin = getCookieManagerPlugin()
    if (!plugin?.flush) {
      console.warn('[cordova][cookie-manager] plugin is unavailable')
      return false
    }

    let timeoutId: ReturnType<typeof setTimeout> | null = null

    return await new Promise<boolean>((resolve) => {
      let isSettled = false

      const finish = (result: boolean) => {
        if (isSettled) return
        isSettled = true

        if (timeoutId) {
          clearTimeout(timeoutId)
          timeoutId = null
        }
        resolve(result)
      }

      timeoutId = setTimeout(() => {
        console.warn('[cordova][cookie-manager] flush timeout')
        finish(false)
      }, FLUSH_TIMEOUT_MS)

      try {
        plugin.flush?.(
          undefined,
          () => finish(true),
          (e) => {
            console.warn('[cordova][cookie-manager] flush failed', e)
            finish(false)
          },
        )
      } catch (e) {
        console.warn('[cordova][cookie-manager] flush failed', e)
        finish(false)
      }
    })
  })().finally(() => {
    flushInFlightPromise = null
  })

  return await flushInFlightPromise
}

export const initCordovaCookieManager = (): void => {
  if (!IS_CORDOVA_BUILD) return

  void flushCordovaCookies()
    .then((ok) => {
      if (!ok) {
        console.info('[cordova][cookie-manager] init finished without flush')
      }
    })
    .catch((e) => {
      console.warn('[cordova][cookie-manager] init failed', e)
    })
}
